import { ref } from 'vue'
import { toast } from 'vue3-toastify'
import { useI18n } from 'vue-i18n'
import {
  HeicErrorCode,
  isAllowedImageFile,
  normalizeImageFile,
  revokePreviewUrl,
} from '~/utils/normalizeImageFile'

type MediaType = 'photo' | 'video'

interface UploadedFile extends File {
  file_url?: string
  signed_url?: string
  file_name?: string
  uploaded_url?: string
  media_type?: MediaType
  preview_url?: string | null
}

interface UploadedMedia {
  file_name: string
  media_type: MediaType
}

interface SignedUrlResponse {
  fileUrl: string
  filename: string
  signedUrl: string
}

export const useFileUpload = () => {
  const uploadedFiles = ref<UploadedFile[]>([])
  const isUploading = ref(false)
  const hasExceededLimit = ref(false)
  const { t } = useI18n()

  // get signed AWS url using internal server api
  // filepath - /server/api/signed-url.post.ts
  const getSignedUrl = async (file: File): Promise<SignedUrlResponse> => {
    try {
      return await $fetch<SignedUrlResponse>('/api/signed-url/', {
        method: 'POST',
        body: { filename: file.name },
      })
    } catch (error) {
      console.error('Error in getSignedUrl:', error)
      throw error
    }
  }

  const uploadFileToSignedUrl = async (file: File, signedUrl: string) => {
    await $fetch(signedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
      },
      body: file,
    }).catch((error) => {
      console.error(error)
      toast(error, {
        type: 'error',
        position: toast.POSITION.TOP_CENTER,
      })
    })
  }

  const handleFileUpload = async (event: Event, maxFiles = 5) => {
    const input = event.target as HTMLInputElement
    hasExceededLimit.value = false

    if (!input.files?.length) return

    const files = Array.from(input.files)
    const remainingSlots = maxFiles - uploadedFiles.value.length
    const filesToUpload = files.slice(0, remainingSlots)

    if (files.length > remainingSlots) {
      hasExceededLimit.value = true
      toast(t('review.upload.maxFiles'), {
        type: 'error',
        position: toast.POSITION.TOP_CENTER,
      })
    }

    if (filesToUpload.length === 0) return

    isUploading.value = true

    try {
      for (const file of filesToUpload) {
        const isVideo =
          file.type.startsWith('video/') || /\.(mp4|mov)$/i.test(file.name)
        const isImage = await isAllowedImageFile(file)

        if (!isVideo && !isImage) {
          toast(t('review.upload.invalidType'), {
            type: 'error',
            position: toast.POSITION.TOP_CENTER,
          })
          continue
        }

        let fileToUpload = file
        let previewUrl: string | null = null

        if (!isVideo) {
          try {
            const result = await normalizeImageFile(file)
            fileToUpload = result.file
            previewUrl = result.previewUrl
          } catch (error) {
            if (
              error &&
              typeof error === 'object' &&
              'code' in error &&
              error.code === HeicErrorCode.SEQUENCE_NOT_SUPPORTED
            ) {
              toast(t('review.upload.heicSequenceNotSupported'), {
                type: 'error',
                position: toast.POSITION.TOP_CENTER,
              })
            } else {
              toast(t('review.upload.heicConversionFailed'), {
                type: 'error',
                position: toast.POSITION.TOP_CENTER,
              })
            }
            continue
          }
        }

        const signedUrlResponse = await getSignedUrl(fileToUpload)

        if (signedUrlResponse) {
          try {
            await uploadFileToSignedUrl(
              fileToUpload,
              signedUrlResponse.signedUrl
            )

            const uploadedFile = fileToUpload as UploadedFile
            uploadedFile.file_url = signedUrlResponse.fileUrl
            uploadedFile.file_name = signedUrlResponse.filename
            uploadedFile.signed_url = signedUrlResponse.signedUrl
            uploadedFile.media_type = isVideo ? 'video' : 'photo'
            uploadedFile.preview_url = previewUrl
            uploadedFiles.value.push(uploadedFile)
          } catch (uploadError) {
            revokePreviewUrl(previewUrl)
            console.error('Error during file upload:', uploadError)
            toast(t('review.upload.error'), {
              type: 'error',
              position: toast.POSITION.TOP_CENTER,
            })
          }
        }
      }
    } catch (error) {
      console.error('Error during file upload:', error)
      toast(t('review.upload.error'), {
        type: 'error',
        position: toast.POSITION.TOP_CENTER,
      })
    } finally {
      isUploading.value = false
      input.value = ''
    }
  }

  const removeFile = (index: number) => {
    const [removedFile] = uploadedFiles.value.splice(index, 1)
    if (removedFile?.preview_url) {
      revokePreviewUrl(removedFile.preview_url)
    }
  }

  const getFileUrl = (file: UploadedFile) =>
    file.preview_url ?? window.URL.createObjectURL(file)

  const getUploadedMedia = (): UploadedMedia[] => {
    return uploadedFiles.value.map((file) => ({
      file_name: file.file_name || '',
      media_type: file.media_type || 'photo',
    }))
  }

  return {
    uploadedFiles,
    isUploading,
    hasExceededLimit,
    handleFileUpload,
    removeFile,
    getFileUrl,
    getUploadedMedia,
  }
}
